import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import UnitAvailabilityCard from '../components/property/UnitAvailabilityCard';
import PlanViewerModal from '../components/common/PlanViewerModal';
import { mockDevelopmentProjects, DevelopmentProjectType, UnitType, mockAgencies, AgencyType } from '../utils/mockData'; // Added mockAgencies and AgencyType
import {
  Container,
  Grid,
  Avatar, // For Logo
  Typography,
  Box,
  Chip,
  Paper,
  Divider,
  Alert,
  AlertTitle,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardMedia,
  CardContent,
  Link as MuiLink, // Renaming to avoid conflict with RouterLink if used
} from '@mui/material';
import {
  LocationOn,
  Business,
  CheckCircleOutline, // For projectLevelPlans
  ImageOutlined as ImageIcon, // For sitePlanImages type
  ViewInAr as ThreeDIcon, // For 3D site plans
  MapOutlined as TwoDIcon, // For 2D site plans
  Event,
  Construction,
  Phone as PhoneIcon, // For contact phone
  EmailOutlined as EmailIcon, // For contact email
  Language as LanguageIcon, // For website
  LocationCity as LocationCityIcon, // For office address
  DoneAll,
  Storefront,
  CalendarToday, // For Expected Completion
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';


const ProjectDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const theme = useTheme();
  const project = mockDevelopmentProjects.find(p => p.id === projectId);

  const developingCompany = project?.developingCompanyAgencyId
    ? mockAgencies.find(agency => agency.id === project.developingCompanyAgencyId)
    : null;

  // State for PlanViewerModal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [selectedImageTitle, setSelectedImageTitle] = useState('');
  const [selectedImageDescription, setSelectedImageDescription] = useState('');

  const handleOpenModal = (imageUrl: string, title: string, description?: string) => {
    setSelectedImageUrl(imageUrl);
    setSelectedImageTitle(title);
    setSelectedImageDescription(description || '');
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    // Optionally reset image info
    // setSelectedImageUrl('');
    // setSelectedImageTitle('');
    // setSelectedImageDescription('');
  };

  if (!project) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" variant="filled">
          <AlertTitle>Error</AlertTitle>
          Project not found. Please check the ID and try again.
        </Alert>
      </Container>
    );
  }

  const getStatusIcon = (status: string) => {
    if (status.toLowerCase().includes('under construction')) return <Construction />;
    if (status.toLowerCase().includes('now selling')) return <Storefront />;
    if (status.toLowerCase().includes('completed')) return <DoneAll />;
    return <Business />; // Default icon
  };

  const getStatusColor = (status: string): "primary" | "secondary" | "error" | "warning" | "info" | "success" => {
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes('now selling')) return 'success';
    if (lowerStatus.includes('under construction')) return 'warning';
    if (lowerStatus.includes('completed')) return 'primary';
    return 'info';
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Project Header */}
      <Paper elevation={3} sx={{ mb: 4, overflow: 'hidden' }}>
        <Box
          sx={{
            height: { xs: 200, sm: 300, md: 400 },
            width: '100%',
            backgroundImage: `url(${project.imageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
          }}
        >
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            p: { xs: 2, md: 3 },
            background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
            color: 'common.white',
          }}>
            <Typography variant="h3" component="h1" fontWeight={700} gutterBottom>
              {project.projectName}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Business fontSize="small" />
                <Typography variant="h6">{project.companyName}</Typography>
            </Stack>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md>
                    <Stack direction="row" alignItems="center" spacing={1} color="text.secondary" mb={1}>
                        <LocationOn fontSize="small" />
                        <Typography variant="body1">{project.location}</Typography>
                    </Stack>
                     <Typography variant="body1" color="text.secondary" paragraph sx={{mb: {xs: 2, md: 0}}}>
                        {project.projectDescription}
                    </Typography>
                </Grid>
                <Grid item xs={12} md="auto">
                    <Stack spacing={1.5} alignItems={{xs: "flex-start", md: "flex-end"}}>
                        <Chip
                            icon={getStatusIcon(project.status)}
                            label={project.status}
                            color={getStatusColor(project.status)}
                            sx={{ textTransform: 'capitalize', fontSize: '1rem', p:1 }}
                        />
                        <Stack direction="row" alignItems="center" spacing={1} color="text.secondary">
                            <Event fontSize="small" />
                            <Typography variant="body2">Launch: {project.launchDate}</Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1} color="text.secondary">
                            <CalendarToday fontSize="small" />
                            <Typography variant="body2">Completion: {project.expectedCompletionDate}</Typography>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
      </Paper>

      {/* Project Level Plans Section */}
      {project.projectLevelPlans && project.projectLevelPlans.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight={600}>
            Project Features & Amenities
          </Typography>
          <Paper elevation={2} sx={{ p: 2 }}>
            <List dense>
              {project.projectLevelPlans.map((plan, index) => (
                <ListItem key={index}>
                  <ListItemIcon sx={{minWidth: 'auto', mr: 1.5}}>
                    <CheckCircleOutline color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={plan} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>
      )}

      {/* Developer Information Section */}
      {developingCompany && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight={600}>
            Developer Information
          </Typography>
          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={3} alignItems="center">
              {developingCompany.logoUrl && (
                <Grid item xs={12} sm={3} md={2} sx={{ textAlign: 'center' }}>
                  <Avatar
                    src={developingCompany.logoUrl}
                    alt={`${developingCompany.name} Logo`}
                    sx={{ width: 100, height: 100, m: 'auto', border: `1px solid ${theme.palette.divider}` }}
                    variant="rounded"
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={developingCompany.logoUrl ? 9 : 12} md={developingCompany.logoUrl ? 10 : 12}>
                <Typography variant="h6" component="h3" fontWeight={700} gutterBottom>
                  {developingCompany.name}
                </Typography>
                {developingCompany.description && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {developingCompany.description}
                  </Typography>
                )}
                <List dense disablePadding>
                  {developingCompany.contactPhone && (
                    <ListItem disableGutters>
                      <ListItemIcon sx={{minWidth: 36}}> <PhoneIcon color="action" fontSize="small"/> </ListItemIcon>
                      <ListItemText primary={<MuiLink href={`tel:${developingCompany.contactPhone}`}>{developingCompany.contactPhone}</MuiLink>} />
                    </ListItem>
                  )}
                  {developingCompany.contactEmail && (
                    <ListItem disableGutters>
                      <ListItemIcon sx={{minWidth: 36}}> <EmailIcon color="action" fontSize="small"/> </ListItemIcon>
                      <ListItemText primary={<MuiLink href={`mailto:${developingCompany.contactEmail}`}>{developingCompany.contactEmail}</MuiLink>} />
                    </ListItem>
                  )}
                  {developingCompany.website && (
                    <ListItem disableGutters>
                      <ListItemIcon sx={{minWidth: 36}}> <LanguageIcon color="action" fontSize="small"/> </ListItemIcon>
                      <ListItemText primary={<MuiLink href={developingCompany.website} target="_blank" rel="noopener noreferrer">{developingCompany.website}</MuiLink>} />
                    </ListItem>
                  )}
                  {developingCompany.offices && developingCompany.offices.length > 0 && (
                     <ListItem disableGutters>
                        <ListItemIcon sx={{minWidth: 36}}> <LocationCityIcon color="action" fontSize="small"/> </ListItemIcon>
                        <ListItemText primary={`${developingCompany.offices[0].address}, ${developingCompany.offices[0].city}`} />
                      </ListItem>
                  )}
                </List>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}

      {/* Site Plan Images Section */}
      {project.sitePlanImages && project.sitePlanImages.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight={600}>
            Site Plans
          </Typography>
          <Grid container spacing={2}>
            {project.sitePlanImages.map((plan, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={plan.url}
                    alt={plan.description || `Site Plan ${index + 1}`}
                    onClick={() => handleOpenModal(plan.url, `${project.projectName} - Site Plan (${plan.type})`, plan.description)}
                    sx={{
                      objectFit: 'contain',
                      backgroundColor: theme.palette.grey[200],
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.85,
                        backgroundColor: theme.palette.grey[300],
                      }
                    }}
                  />
                  <CardContent sx={{textAlign: 'center', flexGrow: 1}}>
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                      {plan.type === '2D' ? <TwoDIcon color="action" /> : <ThreeDIcon color="action" />}
                      <Typography variant="subtitle2" color="text.secondary">
                        {plan.type} Plan
                      </Typography>
                    </Stack>
                    {plan.description && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{mt: 0.5}}>
                        {plan.description}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Unit Availability Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom fontWeight={600}>
          Unit Availability
        </Typography>
        <Divider sx={{ mb: 3 }} />
        {project.units.length > 0 ? (
          <Grid container spacing={3}>
            {project.units.map((unit: UnitType) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={unit.id}>
                <UnitAvailabilityCard unit={unit} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Alert severity="info">
            Unit information is not available at the moment. Please check back later.
          </Alert>
        )}
      </Box>

      <PlanViewerModal
        open={modalOpen}
        onClose={handleCloseModal}
        imageUrl={selectedImageUrl}
        title={selectedImageTitle}
        description={selectedImageDescription}
      />
    </Container>
  );
};

export default ProjectDetailPage;
